/**
 * País del visitante (ISO alpha-2, MAYÚSCULAS) para las condiciones de reglas/tests.
 * El país sale de la IP → solo lo ve el servidor; el SDK lo pide a /api/geo. Como la
 * config está cacheada en CDN por siteKey (compartida entre visitantes), NO puede
 * llevar el país por-visitante, por eso este endpoint aparte, per-visitante.
 *
 * Coste: lo CACHEAMOS en localStorage (como `tw_anon`/`tw_visits`) → el fetch solo ocurre
 * en cache-miss (≈una vez por visitante), no por página ni por sesión. En toda carga con
 * caché el país está disponible SÍNCRONO en el arranque → las reglas de país aplican al
 * instante, sin ida y vuelta. Sin zod (no engorda el bundle).
 */
const KEY = "tw_country";
const TTL_MS = 24 * 3600 * 1000; // 24h: el país es estable; refrescamos al día siguiente.

/**
 * Lee el país cacheado. Devuelve:
 *   - "XX" (alpha-2) → veredicto conocido (país),
 *   - ""             → veredicto conocido de "desconocido" (IP no resoluble: VPN/interna),
 *   - null           → SIN veredicto (nunca resuelto, o caché caducada) → hay que preguntar.
 * La distinción null vs "" importa para el anti-flicker: solo se enmascara cuando NO hay
 * veredicto (null); un "desconocido" ya resuelto no necesita esperar.
 */
export function readCachedCountry(now = Date.now()): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { c?: unknown; t?: unknown };
    if (typeof p.c !== "string" || typeof p.t !== "number") return null;
    if (now - p.t > TTL_MS) return null; // caducada → tratar como miss (re-preguntar)
    return /^[A-Z]{2}$/.test(p.c) ? p.c : ""; // "" = veredicto conocido de desconocido
  } catch {
    return null; // sin localStorage (modo privado) → siempre miss; se preguntará por red
  }
}

function writeCache(country: string, now = Date.now()): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ c: country, t: now }));
  } catch {
    /* sin localStorage → sin caché (degradación segura: se re-preguntará) */
  }
}

/**
 * Pide el país a /api/geo y lo cachea (incluido "" para no re-preguntar en cada carga a un
 * visitante no resoluble durante el TTL). Nunca lanza: ante cualquier fallo devuelve "".
 */
export async function fetchGeo(origin: string): Promise<string> {
  try {
    const res = await fetch(`${origin}/api/geo`, {
      method: "GET",
      credentials: "omit",
      mode: "cors",
    });
    if (!res.ok) {
      writeCache(""); // 5xx/4xx → cachea "desconocido" (ver catch): no repreguntar en cada carga
      return "";
    }
    const data = (await res.json()) as { country?: unknown };
    const c = typeof data.country === "string" ? data.country.toUpperCase() : "";
    const country = /^[A-Z]{2}$/.test(c) ? c : "";
    writeCache(country);
    return country;
  } catch {
    // Red/CORS caído (adblock, red corporativa) → desconocido. Cacheamos "" para NO repetir el fetch
    // ni el anti-flicker (máscara + espera de 1,2s) en cada carga de este visitante. El TTL (24h) lo
    // reintenta más tarde; el fallo persistente degrada a "sin país" una vez, no en cada página.
    writeCache("");
    return "";
  }
}
